import { FC } from 'react';
import { useFolderPage } from 'src/components/folders/FolderPageContext';
import { EntryResultType, IEntry, IProof } from 'src/ts/interfaces';
import { isUnverifiedOfferLetter } from '../helpers/proofHelpers';
import {
  CustomButton,
  DropdownLabel,
  DropdownSelectWrapper,
  ReportPlatformFeedbackLink,
  SubmissionDropdownWrapper,
  TopBar,
  TopBarContent
} from '../styles';
import CustomDropdown from './CustomDropdown';
import ModernSubmissionDropdown from './ModernSubmissionDropdown';

interface DocumentHeaderProps {
  entry: IEntry;
  proofs: IProof[];
  selectedProofId: string;
  onProofChange: (proofId: string) => void;
  currentProof: IProof | undefined;
  getRulingIcon: (proof: IProof) => string;
  entriesInFolder?: IEntry[];
  setSelectedEntryId: (entryId: EntryId) => void;
  assignedEntry?: IEntry | null;
  entryProofCache?: Record<string, IProof[]>;
  onReportPlatformFeedback?: () => void;
}

export type EntryId = string;

const getEntryResultIcon = (result: EntryResultType) => {
  switch (result) {
    case 'CLEAN':
      return '/assets/images/clean.png';
    case 'EDITED':
      return '/assets/images/edited.png';
    case 'UNDETERMINED':
      return '/assets/images/insufficient.png';
    default:
      return '/assets/images/pending.svg';
  }
};

const DocumentHeader: FC<DocumentHeaderProps> = ({
  entry,
  proofs,
  selectedProofId,
  onProofChange,
  currentProof,
  getRulingIcon,
  entriesInFolder,
  setSelectedEntryId,
  assignedEntry,
  entryProofCache,
  onReportPlatformFeedback
}) => {
  const { setShowSplitEntryModal } = useFolderPage();

  const formatEntryTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return `${date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    })} at ${date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })}`;
  };

  // ITL-2690: exclude unverified offer letters from the doc-ID dropdown so the
  // analyst cannot jump directly to a proof that has no actionable review state.
  // Consistent with the prev/next carousel filter in useDocumentNavigation.
  const docIdOptions = proofs
    .filter((proof) => !isUnverifiedOfferLetter(proof))
    .map((proof) => ({
      value: proof.id,
      label: proof.short_id || proof.id,
      icon: getRulingIcon(proof),
      proofResult: proof.result || 'PENDING'
    }));

  const entriesIdIOptions =
    entriesInFolder?.map((entryItem) => {
      let proofCount: number | undefined;

      if (entryItem.id === entry.id) {
        proofCount = proofs.length;
      } else if (entryProofCache && entryProofCache[entryItem.id]) {
        proofCount = entryProofCache[entryItem.id].length;
      }

      return {
        value: entryItem.id,
        label: formatEntryTimestamp(entryItem.submission_time),
        icon: getEntryResultIcon(entryItem?.result),
        status: entryItem?.result || 'PENDING',
        applicantName: entryItem.applicant?.full_name || 'Unknown',
        documentCount: proofCount
      };
    }) || [];

  return (
    <TopBar>
      <TopBarContent>
        {entriesInFolder && entriesInFolder.length > 1 && (
          <CustomButton
            id="remove-from-folder-button"
            onClick={() => setShowSplitEntryModal?.(true)}
          >
            Remove from Folder
          </CustomButton>
        )}
        <SubmissionDropdownWrapper>
          <ModernSubmissionDropdown
            id="submission-dropdown"
            value={entry.id}
            onChange={(value: EntryId) => {
              setSelectedEntryId(value);
            }}
            options={entriesIdIOptions}
            assignedEntryId={assignedEntry?.id}
            placeholder="Submission"
            width="180px"
          />
        </SubmissionDropdownWrapper>

        <DropdownSelectWrapper>
          <DropdownLabel htmlFor="doc-id-select">Doc ID</DropdownLabel>
          <CustomDropdown
            id="doc-id-select"
            value={selectedProofId}
            onChange={onProofChange}
            options={docIdOptions}
            width="362px"
            direction="down"
          />
        </DropdownSelectWrapper>

        {onReportPlatformFeedback && (
          <ReportPlatformFeedbackLink onClick={onReportPlatformFeedback}>
            Report a bug
          </ReportPlatformFeedbackLink>
        )}
      </TopBarContent>
    </TopBar>
  );
};

export default DocumentHeader;
